import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useStore } from 'hooks';
import { VALIDATION } from 'helpers/constants';
import { Button, ButtonClose } from 'components/ui/form';
import { Input, Select } from 'components/ui/formik';
import { DirectionService, StationService } from 'services';
import styles from './station.module.scss';

const MAX_DAILY_WAGON_LIMIT = 2147483647;

const Station = ({ edit, onClose }) => {
  const { setUser, setLoading } = useStore();
  const [directions, setDirections] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const fetchDirections = async () => {
      const service = new DirectionService({ setUser });
      const pageSize = 100;
      const allDirections = [];
      let page = 0;
      let count = 0;

      do {
        const response = await service.getDirections({ page, limit: pageSize });
        if (!response || !Array.isArray(response.items)) {
          throw new Error('Не удалось получить направления');
        }

        const items = response.items;
        allDirections.push(...items);
        count = Number(response?.count) || allDirections.length;
        page += 1;

        if (!items.length) break;
      } while (allDirections.length < count);

      if (!cancelled) {
        setDirections(allDirections);
      }
    };

    fetchDirections().catch(() => {
      if (!cancelled) {
        toast.error('Не удалось загрузить направления');
      }
    });
    return () => { cancelled = true; };
  }, [setUser]);

  const initialValues = {
    name: edit?.name || '',
    code: edit?.code || '',
    country: edit?.country || '',
    region: edit?.region || '',
    dailyWagonLimit: edit?.dailyWagonLimit ?? '',
    directionId: edit?.directionId ?? '',
  };

  const onSubmit = async (values, { setErrors }) => {
    setLoading(true);
    try {
      const service = new StationService({ setUser });
      const body = {
        ...values,
        country: values.country.trim() || null,
        region: values.region.trim() || null,
        dailyWagonLimit: values.dailyWagonLimit === '' ? null : values.dailyWagonLimit,
        directionId: values.directionId === '' ? null : Number(values.directionId),
      };
      const response = edit
        ? await service.updateStation(edit.id, body)
        : await service.createStation(body);

      if (response?.__httpError) {
        const errors = (response.errors || []).reduce((result, error) => ({
          ...result,
          [error.path]: error.msg,
        }), {});
        setErrors(errors);
      } else if (response) {
        toast.success(`Станция «${values.name.trim()}» ${edit ? 'обновлена' : 'добавлена'}`);
        onClose(true);
      }
    } catch {
      toast.error('Не удалось сохранить станцию');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={Yup.object().shape({
        name: Yup.string()
          .trim()
          .max(...VALIDATION.MAX(255, VALIDATION.MAX_STRING))
          .required(...VALIDATION.REQUIRED),
        code: Yup.string()
          .trim()
          .max(...VALIDATION.MAX(255, VALIDATION.MAX_STRING))
          .required(...VALIDATION.REQUIRED),
        country: Yup.string()
          .trim()
          .max(...VALIDATION.MAX(255, VALIDATION.MAX_STRING)),
        region: Yup.string()
          .trim()
          .max(...VALIDATION.MAX(255, VALIDATION.MAX_STRING)),
        dailyWagonLimit: Yup.number()
          .transform((value, originalValue) => (originalValue === '' ? null : value))
          .nullable()
          .integer('Введите целое число')
          .min(0, 'Не меньше 0')
          .max(MAX_DAILY_WAGON_LIMIT, 'Не больше 2147483647'),
      })}
    >
      {({ handleSubmit }) => (
        <form onSubmit={handleSubmit} className={styles.contact} autoComplete="off">
          <div className={styles.main}>
            <div className={styles.title}>
              {edit ? 'Редактирование станции' : 'Добавление станции'}
            </div>
            <div className={styles.fields}>
              <div className={styles.field}>
                <Input label="Название" placeholder="Название" name="name" />
              </div>
              <div className={styles.field}>
                <Input label="Код" placeholder="Код" name="code" />
              </div>
              <div className={styles.field}>
                <Input label="Страна" placeholder="Страна" name="country" />
              </div>
              <div className={styles.field}>
                <Input label="Регион" placeholder="Регион" name="region" />
              </div>
              <div className={styles.field}>
                <Select label="Направление" name="directionId">
                  <option value="">Без направления</option>
                  {directions.map(direction => (
                    <option value={direction.id} key={direction.id}>
                      {direction.name}{direction.code ? ` (${direction.code})` : ''}
                    </option>
                  ))}
                </Select>
              </div>
              <div className={styles.field}>
                <Input
                  label="Суточное ограничение количества вагонов"
                  placeholder="Пусто — без ограничения"
                  name="dailyWagonLimit"
                  type="number"
                  min={0}
                  max={MAX_DAILY_WAGON_LIMIT}
                  step={1}
                />
              </div>
            </div>
          </div>
          <div className={styles.buttons}>
            <div>
              <Button type="submit">{edit ? 'Редактировать' : 'Добавить'}</Button>
            </div>
            <div>
              <ButtonClose onClick={() => onClose(false)}>{edit ? 'Закрыть' : 'Отменить'}</ButtonClose>
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
};

export default Station;
